// ---------------------------------------------------------------------------
// ENVOYER CE QUI A CHANGÉ, PAS TOUT L'ÉTAT.
//
// ⚠️ LE DÉFAUT EST EN N², ET IL NE SE VOIT PAS À QUARANTE.
//
// Mesuré sur le vrai serveur (`essais/cout-seance.mjs`) : l'état d'une séance
// pèse environ 290 octets par participant, et il part à CHACUN d'eux à chaque
// changement. Le coût d'une diffusion croît donc comme le carré du nombre de
// personnes :
//
//     40 personnes   →     451 Ko par diffusion
//    250 personnes   →      17 Mo
//  1 000 personnes   →     282 Mo   ·   9,2 Gbit/s à quatre diffusions/seconde
//
// À mille, la seule synchronisation demanderait plus de bande passante que le
// flux vidéo lui-même — et davantage qu'une fibre à 7 Gbit/s. Ce n'est donc pas
// la vidéo qui empêche de lever `MAX_PAR_INSTANCE`, c'est cette ligne-là.
//
// Or presque tous les changements ne touchent QU'UNE personne : elle avance
// dans l'épisode, elle coupe son micro, elle arrive, elle part. Renvoyer les
// neuf cent quatre-vingt-dix-neuf autres à chaque fois est du gaspillage pur.
//
// ═══ CE QUE CE MODULE FAIT, ET CE QU'IL NE FAIT PAS ════════════════════════
//
// Il calcule la différence entre deux vues. Il ne décide pas de l'envoyer :
// c'est l'appelant qui compare les tailles et tranche — parce qu'une
// différence plus grosse que l'état entier existe (tout le monde arrive en
// même temps), et qu'il serait absurde de l'envoyer alors.
//
// ⚠️ ET IL NUMÉROTE. Une différence ne veut rien dire sans savoir de quoi elle
// part : un client qui a manqué un message appliquerait le delta suivant sur
// un état périmé et dériverait en silence — le pire cas, parce que rien ne le
// signale. Chaque vue porte donc un numéro, chaque delta dit sur quel numéro
// il s'applique, et un client dont le numéro ne correspond pas redemande
// l'état entier.
// ---------------------------------------------------------------------------

/** Les champs de la vue qui ne sont pas la liste des participants. */
const memeValeur = (a, b) => {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  // Comparaison structurelle : ces valeurs sont de petits objets JSON déjà
  // sérialisables, et elles le seront de toute façon en partant.
  return JSON.stringify(a) === JSON.stringify(b);
};

/**
 * La différence entre deux vues de séance.
 *
 * Rend `null` quand rien n'a bougé — et c'est un cas fréquent, pas une
 * curiosité : le battement recompose l'état plusieurs fois par seconde, et la
 * plupart du temps il est identique. Ne rien envoyer coûte alors zéro.
 *
 * La forme :
 *   {
 *     type: 'seance-delta',
 *     de: <numéro de la vue de départ>,
 *     a:  <numéro de la vue d'arrivée>,
 *     champs: { … },                       // seulement ceux qui ont changé
 *     gens: { ajoutes: [], retires: [], modifies: [] },
 *   }
 */
export function calculerDelta(avant, apres) {
  if (!avant || !apres) return null;

  const champs = {};
  for (const [cle, valeur] of Object.entries(apres)) {
    if (cle === 'participants' || cle === 'v') continue;
    if (!memeValeur(avant[cle], valeur)) champs[cle] = valeur;
  }

  // ⚠️ UN CHAMP DISPARU N'EST PAS UN CHAMP INCHANGÉ.
  //
  // Si `avant` portait une clé que `apres` n'a plus, la boucle ci-dessus ne la
  // voit pas — elle ne parcourt que `apres`. Le client la garderait pour
  // toujours. On la marque donc explicitement à `null`.
  for (const cle of Object.keys(avant)) {
    if (cle === 'participants' || cle === 'v') continue;
    if (!(cle in apres)) champs[cle] = null;
  }

  const parId = (liste) => new Map((liste ?? []).map((p) => [p.id, p]));
  const gensAvant = parId(avant.participants);
  const gensApres = parId(apres.participants);

  const ajoutes = [];
  const modifies = [];
  for (const [id, p] of gensApres) {
    const ancien = gensAvant.get(id);
    if (!ancien) { ajoutes.push(p); continue; }
    // Seuls les champs qui bougent partent. Une position qui avance, c'est
    // deux champs sur quinze — le reste n'a aucune raison de repasser.
    const bouge = {};
    for (const [cle, valeur] of Object.entries(p)) {
      if (!memeValeur(ancien[cle], valeur)) bouge[cle] = valeur;
    }
    if (Object.keys(bouge).length) modifies.push({ id, ...bouge });
  }
  const retires = [...gensAvant.keys()].filter((id) => !gensApres.has(id));

  const rienDeNeuf = !Object.keys(champs).length
    && !ajoutes.length && !modifies.length && !retires.length;
  if (rienDeNeuf) return null;

  return {
    type: 'seance-delta',
    de: avant.v ?? null,
    a: apres.v ?? null,
    champs,
    gens: { ajoutes, retires, modifies },
  };
}

/**
 * Applique une différence — côté client, et côté banc d'essai.
 *
 * Rend `null` si le delta ne part pas de la vue qu'on a : l'appelant doit
 * alors redemander l'état entier plutôt que d'appliquer à l'aveugle. C'est
 * cette porte-là qui empêche la dérive silencieuse.
 */
export function appliquerDelta(vue, delta) {
  if (!vue || !delta) return null;
  if (delta.de !== null && delta.de !== undefined && vue.v !== delta.de) return null;

  const suivante = { ...vue, ...delta.champs, v: delta.a ?? vue.v };
  for (const [cle, valeur] of Object.entries(delta.champs ?? {})) {
    if (valeur === null && !(cle in vue)) delete suivante[cle];
  }

  const gens = new Map((vue.participants ?? []).map((p) => [p.id, p]));
  for (const id of delta.gens?.retires ?? []) gens.delete(id);
  for (const p of delta.gens?.ajoutes ?? []) gens.set(p.id, p);
  for (const p of delta.gens?.modifies ?? []) {
    const ancien = gens.get(p.id);
    // Un « modifié » sur quelqu'un qu'on ne connaît pas ne s'invente pas à
    // moitié : on le pose tel quel, et le prochain état complet le corrigera.
    gens.set(p.id, ancien ? { ...ancien, ...p } : p);
  }
  suivante.participants = [...gens.values()];
  return suivante;
}

/**
 * Ce qu'il faut envoyer : la différence si elle est plus courte, l'état sinon.
 *
 * ⚠️ LE SEUIL N'EST PAS À CENT POUR CENT. Une différence qui pèse 95 % de
 * l'état ne vaut pas la complexité qu'elle ajoute — et elle multiplie les
 * occasions de désynchronisation pour cinq pour cent de gain. En dessous de
 * `part`, on envoie la différence ; au-dessus, l'état entier, qui a le mérite
 * de remettre tout le monde d'accord.
 */
export function choisirEnvoi(avant, apres, { part = 0.6 } = {}) {
  if (!avant) return { quoi: apres, complet: true, raison: 'premier envoi' };
  const delta = calculerDelta(avant, apres);
  if (!delta) return { quoi: null, complet: false, raison: 'rien n’a changé' };

  const tailleDelta = JSON.stringify(delta).length;
  const tailleEtat = JSON.stringify(apres).length;
  if (tailleDelta < tailleEtat * part) {
    return { quoi: delta, complet: false, raison: 'différence', tailleDelta, tailleEtat };
  }
  return { quoi: apres, complet: true, raison: 'différence trop grosse', tailleDelta, tailleEtat };
}
